import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const listingChecklistApi = createApi({
  reducerPath: "listingChecklistApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ListingChecklists", "ChecklistStats"],
  endpoints: (builder) => ({
    // Get all checklists
    getAllChecklists: builder.query({
      query: (params) => ({
        url: "/listing-checklists",
        params,
      }),
      providesTags: ["ListingChecklists"],
    }),

    // Get checklist stats for dashboard
    getChecklistStats: builder.query({
      query: () => "/listing-checklists/stats",
      providesTags: ["ChecklistStats"],
    }),

    // Get single checklist
    getChecklistById: builder.query({
      query: (id) => `/listing-checklists/${id}`,
      providesTags: (result, error, id) => [{ type: "ListingChecklists", id }],
    }),

    // Create checklist
    createChecklist: builder.mutation({
      query: (data) => ({
        url: "/listing-checklists",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ListingChecklists", "ChecklistStats"],
    }),

    // Update checklist
    updateChecklist: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/listing-checklists/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ListingChecklists", id },
        "ListingChecklists",
        "ChecklistStats",
      ],
    }),

    // Update checklist item
    updateChecklistItem: builder.mutation({
      query: ({ id, section, itemId, completed, notes, dueDate, assignedTo, priority }) => ({
        url: `/listing-checklists/${id}/item`,
        method: "PATCH",
        body: { section, itemId, completed, notes, dueDate, assignedTo, priority },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ListingChecklists", id },
        "ListingChecklists",
        "ChecklistStats",
      ],
    }),

    // Add custom item
    addCustomItem: builder.mutation({
      query: ({ id, text, section, dueDate, assignedTo, priority }) => ({
        url: `/listing-checklists/${id}/item`,
        method: "POST",
        body: { text, section, dueDate, assignedTo, priority },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ListingChecklists", id },
        "ListingChecklists",
        "ChecklistStats",
      ],
    }),

    // Archive checklist
    archiveChecklist: builder.mutation({
      query: (id) => ({
        url: `/listing-checklists/${id}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: ["ListingChecklists", "ChecklistStats"],
    }),

    // Delete checklist
    deleteChecklist: builder.mutation({
      query: (id) => ({
        url: `/listing-checklists/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ListingChecklists", "ChecklistStats"],
    }),
  }),
});

export const {
  useGetAllChecklistsQuery,
  useGetChecklistStatsQuery,
  useGetChecklistByIdQuery,
  useCreateChecklistMutation,
  useUpdateChecklistMutation,
  useUpdateChecklistItemMutation,
  useAddCustomItemMutation,
  useArchiveChecklistMutation,
  useDeleteChecklistMutation,
} = listingChecklistApi;
